import { useEffect, useState } from "react";
import AppointmentCalendar from "./AppointmentCalender";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Cell, ResponsiveContainer,
} from "recharts";

const API = process.env.REACT_APP_API_URL || "https://doctor-appointment-1-yf2j.onrender.com";


export default function UserDashboard() {
  const [stats, setStats] = useState({ upcoming: 0, completed: 0, cancelled: 0 });
  const [upcoming, setUpcoming] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [search, setSearch] = useState("");
  const [userProfile, setUserProfile] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);

  let user = null;
  try {
    const raw = localStorage.getItem("user");
    user = raw && raw !== "undefined" ? JSON.parse(raw) : null;
  } catch {
    user = null;
  }
  const userId = user?.id || user?.userId;

  useEffect(() => {
    if (!userId) return;
    fetch(`${API}/api/appointments/user/stats/${userId}`)
      .then((r) => r.json()).then(setStats).catch(() => {});
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    fetch(`${API}/api/appointments/user/${userId}`)
      .then((r) => r.json())
      .then((data) => {
        let appts = Array.isArray(data) ? data : data.data ?? data.appointments ?? [];
        const now = new Date();
        const dt = (a) => { const d = new Date(a.date); if (a.time) { const [h,m]=a.time.split(":").map(Number); d.setHours(h,m,0,0); } return d; };
        setUpcoming(appts.filter(a => a.status !== "cancelled" && a.status !== "completed" && dt(a) >= now));
      }).catch(() => setUpcoming([]));
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    fetch(`${API}/api/users/profile/${userId}`)
      .then((r) => r.json()).then(setUserProfile).catch(() => {});
  }, [userId]);

  useEffect(() => {
    fetch(`${API}/api/doctors/all`)
      .then((r) => r.json())
      .then((data) => {
        const userCity = (userProfile?.city || user?.city || "").trim().toLowerCase();
        const userPin = (userProfile?.pincode || user?.pincode || "").trim();

        const nearbyDoctors = data.filter((doc) => {
          const docCity = (doc.city && doc.city !== "null" && doc.city !== "undefined") ? doc.city.trim().toLowerCase() : "";
          const docArea = (doc.area && doc.area !== "null" && doc.area !== "undefined") ? doc.area.trim().toLowerCase() : "";
          const docPin = (doc.pincode && doc.pincode !== "null" && doc.pincode !== "undefined") ? doc.pincode.trim() : "";

          if (!docCity && !docArea && !docPin) return false;

          let isPinMatch = false;
          if (userPin && docPin) {
            isPinMatch = docPin === userPin || docPin.slice(0, 3) === userPin.slice(0, 3);
          }

          let isCityMatch = false;
          if (userCity && (docCity || docArea)) {
            isCityMatch = (docCity && (docCity === userCity || docCity.includes(userCity) || userCity.includes(docCity))) ||
                          (docArea && (docArea === userCity || docArea.includes(userCity) || userCity.includes(docArea)));
          }

          if (userCity || userPin) {
            return isPinMatch || isCityMatch;
          }
          return true;
        });

        const sortedDoctors = [...nearbyDoctors].sort((a, b) => {
          const pinA = (a.pincode && a.pincode !== "null" && a.pincode !== "undefined") ? a.pincode.trim() : "";
          const pinB = (b.pincode && b.pincode !== "null" && b.pincode !== "undefined") ? b.pincode.trim() : "";
          const cityA = (a.city && a.city !== "null" && a.city !== "undefined") ? a.city.trim().toLowerCase() : "";
          const cityB = (b.city && b.city !== "null" && b.city !== "undefined") ? b.city.trim().toLowerCase() : "";

          if (userPin) {
            const numUser = parseInt(userPin) || 0;
            const diffA = pinA ? Math.abs(parseInt(pinA || 0) - numUser) : 9999999;
            const diffB = pinB ? Math.abs(parseInt(pinB || 0) - numUser) : 9999999;
            if (diffA !== diffB) return diffA - diffB;
          }

          if (userCity) {
            const matchA = cityA === userCity ? 0 : 1;
            const matchB = cityB === userCity ? 0 : 1;
            if (matchA !== matchB) return matchA - matchB;
          }

          return 0;
        });

        setDoctors(sortedDoctors);
      }).catch(() => {});
  }, [userProfile]);

  useEffect(() => {
    if (!user?.id) return;
    fetch(`${API}/api/notifications/user/${user.id}`)
      .then((r) => r.json()).then(setNotifications).catch(() => {});
  }, [user.id]);

  const markAsRead = async (id) => {
    try {
      await fetch(`${API}/api/notifications/read/${id}`, { method: "POST" });
      setNotifications(notifications.map(n => n.id === id ? { ...n, isRead: 1 } : n));
    } catch {}
  };

  const chartData = [
    { status: "Upcoming", count: stats.upcoming, color: "#6366f1" },
    { status: "Completed", count: stats.completed, color: "#10b981" },
    { status: "Cancelled", count: stats.cancelled, color: "#f43f5e" },
  ];

  const unreadCount = notifications.filter(n => !n.isRead).length;
  const initials = (name) => (name || "U").split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);

  return (
    <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", background: "#f8f7ff", minHeight: "100vh" }}>
      <style>{`
        .dash-container { max-width: 1200px; margin: 0 auto; padding: 32px 24px; display: flex; flex-direction: column; gap: 24px; }
        .dash-hero { background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #a855f7 100%); border-radius: 24px; padding: 36px 40px; display: flex; justify-content: space-between; align-items: center; overflow: hidden; position: relative; }
        .dash-stats-group { display: flex; gap: 12px; position: relative; z-index: 1; }
        .dash-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
        .dash-card { background: #fff; border-radius: 20px; padding: 24px 28px; border: 1px solid #ede9fe; }
        @media (max-width: 900px) {
          .dash-grid { grid-template-columns: 1fr; }
          .dash-hero { flex-direction: column; align-items: stretch; gap: 24px; padding: 28px 24px; }
        }
        @media (max-width: 600px) {
          .dash-container { padding: 16px 12px; }
          .dash-stats-group { flex-direction: column; gap: 8px; }
          .dash-stats-group > div { padding: 12px 16px; }
          .dash-card { padding: 16px 20px; }
        }
      `}</style>

      {/* NAV */}
      <nav style={{
        background: "#fff", borderBottom: "1px solid #ede9fe",
        padding: "0 32px", height: 64,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        position: "sticky", top: 0, zIndex: 40,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10,
            background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round">
              <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
            </svg>
          </div>
          <span style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: 20, fontWeight: 800, color: "#1e1b4b", letterSpacing: "-0.5px" }}>
            MediBook
          </span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {/* Bell */}
          <div style={{ position: "relative" }}>
            <button onClick={() => setNotifOpen(o => !o)} style={{
              width: 40, height: 40, borderRadius: 10, border: "1px solid #ede9fe",
              background: notifOpen ? "#f5f3ff" : "#fff",
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer",
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2" strokeLinecap="round">
                <path d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 00-5-5.917V4a1 1 0 10-2 0v1.083A6 6 0 006 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              {unreadCount > 0 && (
                <span style={{
                  position: "absolute", top: -4, right: -4,
                  background: "#f43f5e", color: "#fff", borderRadius: "50%",
                  width: 18, height: 18, fontSize: 10, fontWeight: 700,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  border: "2px solid #fff",
                }}>{unreadCount}</span>
              )}
            </button>

            {notifOpen && (
              <div style={{
                position: "absolute", right: 0, top: "calc(100% + 8px)",
                width: 320, background: "#fff", border: "1px solid #ede9fe",
                borderRadius: 16, overflow: "hidden",
                boxShadow: "0 20px 60px rgba(99,102,241,0.12)", zIndex: 50,
              }}>
                <div style={{ padding: "14px 16px", borderBottom: "1px solid #f5f3ff", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span style={{ fontWeight: 700, fontSize: 14, color: "#1e1b4b" }}>Notifications</span>
                  {unreadCount > 0 && <span style={{ fontSize: 11, color: "#6366f1", fontWeight: 600 }}>{unreadCount} unread</span>}
                </div>
                <div style={{ maxHeight: 300, overflowY: "auto" }}>
                  {notifications.length === 0
                    ? <p style={{ textAlign: "center", padding: 24, color: "#a5b4fc", fontSize: 13 }}>All caught up!</p>
                    : notifications.map(n => (
                      <div key={n.id} style={{
                        padding: "12px 16px", borderBottom: "1px solid #fafafa",
                        background: n.isRead ? "#fff" : "#faf9ff",
                        display: "flex", gap: 10,
                      }}>
                        <div style={{
                          width: 8, height: 8, borderRadius: "50%", marginTop: 5, flexShrink: 0,
                          background: n.isRead ? "#e2e8f0" : "#6366f1",
                        }} />
                        <div style={{ flex: 1 }}>
                          <p style={{ fontSize: 13, color: "#374151", margin: 0, lineHeight: 1.5 }}>{n.message}</p>
                          {!n.isRead && (
                            <button onClick={() => markAsRead(n.id)} style={{ fontSize: 11, color: "#6366f1", fontWeight: 600, marginTop: 4, background: "none", border: "none", cursor: "pointer", padding: 0 }}>
                              Mark as read
                            </button>
                          )}
                        </div>
                      </div>
                    ))
                  }
                </div>
              </div>
            )}
          </div>

          {/* Avatar chip */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 10px", borderRadius: 10, border: "1px solid #ede9fe" }}>
            <div style={{
              width: 32, height: 32, borderRadius: 8,
              background: "linear-gradient(135deg, #818cf8, #6366f1)",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "#fff", fontWeight: 700, fontSize: 13,
            }}>{initials(user?.name)}</div>
            <div>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#1e1b4b" }}>{user?.name || "User"}</p>
              <p style={{ margin: 0, fontSize: 11, color: "#a5b4fc" }}>Patient</p>
            </div>
          </div>
        </div>
      </nav>

      {/* PAGE */}
      <div className="dash-container">

        {/* HERO */}
        <div className="dash-hero">
          <div style={{ position: "absolute", width: 200, height: 200, borderRadius: "50%", background: "rgba(255,255,255,0.06)", top: -60, right: 120 }} />
          <div style={{ position: "absolute", width: 140, height: 140, borderRadius: "50%", background: "rgba(255,255,255,0.06)", bottom: -40, right: 60 }} />
          <div style={{ position: "absolute", width: 80, height: 80, borderRadius: "50%", background: "rgba(255,255,255,0.08)", top: 20, right: 280 }} />
          <div style={{ position: "relative", zIndex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#a7f3d0", boxShadow: "0 0 0 3px rgba(167,243,208,0.3)" }} />
              <span style={{ fontSize: 12, color: "#c4b5fd", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase" }}>Patient Dashboard</span>
            </div>
            <h1 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: 32, fontWeight: 800, color: "#fff", margin: "0 0 8px", letterSpacing: "-0.5px" }}>
              Welcome back, {user?.name?.split(" ")[0] || "User"} 👋
            </h1>
            <p style={{ color: "#c4b5fd", margin: 0, fontSize: 14 }}>Here's your health snapshot for today.</p>
          </div>
          {/* Stat pills */}
          <div className="dash-stats-group">
            {[
              { label: "Upcoming", val: stats.upcoming, col: "#a5f3fc" },
              { label: "Completed", val: stats.completed, col: "#a7f3d0" },
              { label: "Cancelled", val: stats.cancelled, col: "#fda4af" },
            ].map(s => (
              <div key={s.label} style={{
                background: "rgba(255,255,255,0.12)",
                border: "1px solid rgba(255,255,255,0.2)",
                borderRadius: 16, padding: "16px 20px", textAlign: "center", minWidth: 80,
              }}>
                <p style={{ margin: 0, fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: 30, fontWeight: 800, color: s.col }}>{s.val}</p>
                <p style={{ margin: "4px 0 0", fontSize: 11, color: "rgba(255,255,255,0.65)", fontWeight: 600 }}>{s.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CHART + UPCOMING */}
        <div className="dash-grid">

          {/* Chart card */}
          <div className="dash-card">
            <p style={{ margin: "0 0 2px", fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: 17, fontWeight: 800, color: "#1e1b4b" }}>Appointment Stats</p>
            <p style={{ margin: "0 0 20px", fontSize: 13, color: "#94a3b8" }}>Overview of all your appointments</p>
            <ResponsiveContainer width="100%" height={200} minWidth={0} minHeight={0}>
              <BarChart data={chartData} barSize={36} margin={{ top: 0, right: 0, left: -24, bottom: 0 }}>
                <XAxis dataKey="status" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#94a3b8" }} />
                <YAxis allowDecimals={false} axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#94a3b8" }} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #ede9fe", fontSize: 13, boxShadow: "0 8px 30px rgba(99,102,241,0.1)" }} cursor={{ fill: "#f5f3ff" }} />
                <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                  {chartData.map((e, i) => <Cell key={i} fill={e.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div style={{ display: "flex", gap: 16, marginTop: 8 }}>
              {chartData.map(d => (
                <div key={d.status} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{ width: 10, height: 10, borderRadius: 3, background: d.color }} />
                  <span style={{ fontSize: 12, color: "#64748b" }}>{d.status}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Upcoming card */}
          <div className="dash-card">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
              <div>
                <p style={{ margin: "0 0 2px", fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: 17, fontWeight: 800, color: "#1e1b4b" }}>Upcoming</p>
                <p style={{ margin: 0, fontSize: 13, color: "#94a3b8" }}>{upcoming.length} appointment{upcoming.length !== 1 ? "s" : ""} scheduled</p>
              </div>
              <div style={{ background: "#f5f3ff", borderRadius: 10, padding: "6px 12px", fontSize: 12, fontWeight: 700, color: "#6366f1" }}>{upcoming.length} total</div>
            </div>
            {upcoming.length === 0 ? <EmptyState text="No upcoming appointments" /> : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10, maxHeight: 240, overflowY: "auto" }}>
                {upcoming.map((appt, idx) => (
                  <div key={appt.id} style={{
                    display: "flex", alignItems: "center", gap: 14,
                    padding: "12px 14px", borderRadius: 14,
                    border: "1px solid #f1f5f9",
                    background: idx % 2 === 0 ? "#fafbff" : "#fff",
                  }}>
                    <div style={{
                      width: 42, height: 42, borderRadius: 12, flexShrink: 0,
                      background: "linear-gradient(135deg, #e0e7ff, #c7d2fe)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 15, fontWeight: 800, color: "#4f46e5",
                    }}>{initials(appt.doctor_name)}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#1e1b4b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{appt.doctor_name}</p>
                      <p style={{ margin: "2px 0 0", fontSize: 12, color: "#94a3b8" }}>{appt.date} · {appt.time}</p>
                      {appt.reason && (
                        <p style={{ margin: "4px 0 0", fontSize: 12, color: "#64748b", fontStyle: "italic", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          Reason: {appt.reason}
                        </p>
                      )}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end" }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: "#4f46e5", background: "#ede9fe", borderRadius: 8, padding: "4px 10px", whiteSpace: "nowrap" }}>Scheduled</span>
                      {appt.meeting_link && (
                        <a
                          href={appt.meeting_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: "#ffffff",
                            background: "linear-gradient(135deg, #0284c7, #0369a1)",
                            borderRadius: 8,
                            padding: "5px 12px",
                            textDecoration: "none",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            boxShadow: "0 2px 6px rgba(2, 132, 199, 0.3)",
                            whiteSpace: "nowrap"
                          }}
                        >
                          🎥 Join Meet
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* CALENDAR */}
        <div className="dash-card">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
            <div>
              <p style={{ margin: "0 0 2px", fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: 17, fontWeight: 800, color: "#1e1b4b" }}>Calendar View</p>
              <p style={{ margin: 0, fontSize: 13, color: "#94a3b8" }}>Your appointment schedule</p>
            </div>
            <div style={{ display: "flex", gap: 14 }}>
              {[{ col: "#6366f1", label: "Upcoming" }, { col: "#10b981", label: "Completed" }, { col: "#f43f5e", label: "Cancelled" }].map(l => (
                <div key={l.label} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{ width: 10, height: 10, borderRadius: 3, background: l.col }} />
                  <span style={{ fontSize: 12, color: "#64748b" }}>{l.label}</span>
                </div>
              ))}
            </div>
          </div>
          <AppointmentCalendar />
        </div>

        {/* NEARBY DOCTORS */}
        <div className="dash-card">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
            <div>
              <p style={{ margin: "0 0 2px", fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: 17, fontWeight: 800, color: "#1e1b4b" }}>Nearby Doctors</p>
              <p style={{ margin: 0, fontSize: 13, color: "#94a3b8" }}>Doctors available in your city</p>
            </div>
            <div style={{ position: "relative" }}>
              <svg style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2.5" strokeLinecap="round">
                <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
              </svg>
              <input
                placeholder="Search specialization..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{
                  paddingLeft: 34, paddingRight: 14, paddingTop: 10, paddingBottom: 10,
                  fontSize: 13, borderRadius: 12, border: "1px solid #ede9fe",
                  background: "#faf9ff", outline: "none", width: 220,
                  fontFamily: "'Plus Jakarta Sans', sans-serif", color: "#1e1b4b",
                }}
              />
            </div>
          </div>

          {(() => {
            const filtered = doctors.filter(d => d.specialization?.toLowerCase().includes(search.toLowerCase()));
            const palette = [
              { topBg: "linear-gradient(135deg,#ede9fe,#ddd6fe)", avatarBg: "rgba(255,255,255,0.55)", avatarText: "#5b21b6", nameTxt: "#3730a3" },
              { topBg: "linear-gradient(135deg,#d1fae5,#a7f3d0)", avatarBg: "rgba(255,255,255,0.55)", avatarText: "#065f46", nameTxt: "#064e3b" },
              { topBg: "linear-gradient(135deg,#fce7f3,#fbcfe8)", avatarBg: "rgba(255,255,255,0.55)", avatarText: "#9d174d", nameTxt: "#831843" },
              { topBg: "linear-gradient(135deg,#fef3c7,#fde68a)", avatarBg: "rgba(255,255,255,0.55)", avatarText: "#92400e", nameTxt: "#78350f" },
              { topBg: "linear-gradient(135deg,#e0f2fe,#bae6fd)", avatarBg: "rgba(255,255,255,0.55)", avatarText: "#0c4a6e", nameTxt: "#0369a1" },
              { topBg: "linear-gradient(135deg,#fee2e2,#fecaca)", avatarBg: "rgba(255,255,255,0.55)", avatarText: "#7f1d1d", nameTxt: "#991b1b" },
            ];
            return filtered.length === 0 ? <EmptyState text="No doctors found for that specialization" /> : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(230px,1fr))", gap: 16 }}>
                {filtered.map((doc, i) => {
                  const c = palette[i % palette.length];
                  return (
                    <DoctorCard key={doc.id} doc={doc} palette={c} initials={initials} userPin={userProfile?.pincode || user?.pincode} userCity={userProfile?.city || user?.city} />
                  );
                })}
              </div>
            );
          })()}
        </div>

      </div>
    </div>
  );
}

function DoctorCard({ doc, palette: c, initials, userPin, userCity }) {
  const [hovered, setHovered] = useState(false);
  const docPin = (doc?.pincode && doc.pincode !== "null" && doc.pincode !== "undefined") ? doc.pincode.trim() : "";
  const docCity = (doc?.city && doc.city !== "null" && doc.city !== "undefined") ? doc.city.trim().toLowerCase() : "";
  const docArea = (doc?.area && doc.area !== "null" && doc.area !== "undefined") ? doc.area.trim().toLowerCase() : "";

  const pinMatch = userPin && docPin && (docPin === userPin.trim() || docPin.slice(0, 3) === userPin.trim().slice(0, 3));
  const cityMatch = userCity && (docCity === userCity.trim().toLowerCase() || docArea === userCity.trim().toLowerCase() || (docCity && userCity.trim().toLowerCase().includes(docCity)) || (docArea && userCity.trim().toLowerCase().includes(docArea)));
  const isNearby = pinMatch || cityMatch;
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        borderRadius: 18, overflow: "hidden", border: "1px solid #f1f5f9",
        background: "#fff",
        transform: hovered ? "translateY(-4px)" : "translateY(0)",
        boxShadow: hovered ? "0 16px 40px rgba(99,102,241,0.12)" : "none",
        transition: "transform 0.18s, box-shadow 0.18s",
      }}
    >
      <div style={{ background: c.topBg, padding: "20px 20px 16px", display: "flex", alignItems: "center", gap: 14 }}>
        <div style={{
          width: 52, height: 52, borderRadius: 14, flexShrink: 0,
          background: c.avatarBg, border: "2px solid rgba(255,255,255,0.8)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 18, fontWeight: 800, color: c.avatarText,
        }}>{initials(doc.name)}</div>
        <div style={{ minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: 15, fontWeight: 800, color: c.nameTxt, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{doc.name}</p>
          <span style={{
            display: "inline-block", marginTop: 4,
            fontSize: 11, fontWeight: 700, color: c.avatarText,
            background: "rgba(255,255,255,0.55)", borderRadius: 6, padding: "2px 8px",
          }}>{doc.specialization}</span>
        </div>
      </div>
      <div style={{ padding: "14px 20px 18px" }}>
        {(docCity || docArea || docPin) && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 13 }}>📍</span>
              <span style={{ fontSize: 12, color: "#475569", fontWeight: 600 }}>{doc.area && doc.area !== "null" && doc.area !== "undefined" ? doc.area : (doc.city && doc.city !== "null" && doc.city !== "undefined" ? doc.city : "")} {docPin ? `(${doc.pincode})` : ""}</span>
            </div>
            {isNearby && (
              <span style={{ fontSize: 10, fontWeight: 800, color: "#059669", background: "#d1fae5", padding: "2px 6px", borderRadius: 6, border: "1px solid #10b981" }}>
                Near You
              </span>
            )}
          </div>
        )}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <span style={{ fontSize: 12, color: "#64748b" }}>Consultation fee</span>
          <span style={{ fontSize: 17, fontWeight: 800, color: "#1e1b4b" }}>₹{doc.consultation_fee}</span>
        </div>
        <button
          onClick={() => window.location.href = `/doctor/${doc.id}`}
          style={{
            width: "100%", padding: "11px 0", borderRadius: 12, border: "none",
            background: "linear-gradient(135deg,#6366f1,#7c3aed)",
            color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer",
            fontFamily: "'Plus Jakarta Sans', sans-serif", letterSpacing: "0.01em",
            opacity: hovered ? 0.9 : 1, transition: "opacity 0.15s",
          }}
        >Book Appointment →</button>
      </div>
    </div>
  );
}

function EmptyState({ text }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "32px 0" }}>
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#c4b5fd" strokeWidth="1.5" strokeLinecap="round">
        <circle cx="12" cy="12" r="10" /><path d="M9 9l6 6M15 9l-6 6" />
      </svg>
      <p style={{ marginTop: 12, fontSize: 13, color: "#94a3b8" }}>{text}</p>
    </div>
  );
}
